import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import axios from "axios";

import API_BASE from "../../lib/config";

const GalleryManagement = () => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    image_url: "",
    caption: "",
    category: "Ambience"
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Helper to format image URLs for admin preview
  const getAdminImageUrl = (image) => {
    const raw = image?.image_url || image?.url || "";
    if (!raw) return "/signature_persian_kabab_platter_1776082069527.png";
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) return raw;
    if (raw.startsWith("/static/")) return `${API_BASE}${raw}`;
    return raw;
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Fetch all images
  const fetchImages = async () => {
    try {
      const token = localStorage.getItem("admin_token");

      const res = await axios.get(`${API_BASE}/api/gallery`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setImages(res.data || []);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to load gallery images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // UPLOAD IMAGE FILE
  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const token = localStorage.getItem("admin_token");
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(
        `${API_BASE}/api/admin/upload/image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        const imageUrl = response.data.url.startsWith("http") ? response.data.url : `${API_BASE}${response.data.url}`;
        setFormData({ ...formData, image_url: imageUrl });
        toast({
          title: "Success",
          description: "Image uploaded successfully!",
        });
      }
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // Add new image
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");

    try {
      await axios.post(`${API_BASE}/api/admin/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast({
        title: "Success",
        description: "Image added successfully!",
      });

      setFormData({ image_url: "", caption: "", category: "Ambience" });
      setShowAddForm(false);
      fetchImages();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to add image",
        variant: "destructive",
      });
    }
  };

  // Delete image
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image permanently?")) return;

    try {
      const token = localStorage.getItem("admin_token");

      await axios.delete(`${API_BASE}/api/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast({
        title: "Deleted",
        description: "Image removed successfully",
      });

      fetchImages();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-primary">Loading gallery...</div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2
            className="text-4xl font-bold text-primary"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Gallery Management
          </h2>
          <p className="text-gray-300 mt-2">Total Images: {images.length}</p>
        </div>

        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-primary hover:bg-[#CA8A04] text-black px-6 py-3 rounded-full font-bold flex items-center space-x-2"
        >
          {showAddForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          <span>{showAddForm ? "Cancel" : "Add New Image"}</span>
        </button>
      </div>

      {/* Add Image Form */}
      {showAddForm && (
        <div className="bg-[#1a4855] border border-[#EAB308]/20 rounded-2xl p-6 mb-8">
          <h3 className="text-2xl font-bold text-primary mb-4">
            Add New Image
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-primary font-semibold mb-2">
                Image *
              </label>
              
              {/* File Upload Option */}
              <div className="mb-3">
                <label className="block text-sm text-gray-400 mb-2">
                  Upload from your device:
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(file);
                    }
                  }}
                  className="w-full px-4 py-2 bg-[#0f2933] border border-[#EAB308]/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-black file:cursor-pointer hover:file:bg-[#d4a406]"
                  disabled={uploadingImage}
                />
                {uploadingImage && (
                  <p className="text-sm text-primary mt-1">Uploading image...</p>
                )}
              </div>

              {/* OR divider */}
              <div className="flex items-center my-3">
                <div className="flex-1 border-t border-[#EAB308]/20"></div>
                <span className="px-3 text-gray-500 text-sm">OR</span>
                <div className="flex-1 border-t border-[#EAB308]/20"></div>
              </div>

              {/* URL Input Option */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Enter image URL:
                </label>
                <input
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 bg-[#0f2933] border border-[#EAB308]/20 rounded-lg text-white focus:border-primary/50 focus:outline-none"
                />
              </div>

              {/* Image Preview */}
              {formData.image_url && (
                <div className="mt-3">
                  <p className="text-sm text-gray-400 mb-2">Preview:</p>
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg border border-[#EAB308]/20"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/150?text=Invalid+Image";
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-primary font-semibold mb-2">
                Caption *
              </label>
              <input
                required
                value={formData.caption}
                onChange={(e) =>
                  setFormData({ ...formData, caption: e.target.value })
                }
                placeholder="e.g. Royal Raan Biryani or The Grand Darbar Hall"
                className="w-full px-4 py-2 bg-[#0f2933] border border-[#EAB308]/20 rounded-lg text-white focus:border-primary/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-primary font-semibold mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-full px-4 py-2 bg-[#0f2933] border border-[#EAB308]/20 rounded-lg text-white focus:border-primary/50 focus:outline-none"
              >
                <option value="Ambience">Ambience (Dining Hall, Seating, Décor)</option>
                <option value="Food">Food (Signature Dishes, Platters, Biryani)</option>
                <option value="Royal">Royal (Archways, Art, Mocktails, Chandeliers)</option>
                <option value="Events">Events (Banquets, Family Dastarkhwan, Celebrations)</option>
              </select>
            </div>

            <button
              type="submit"
              className="w-full bg-primary hover:bg-[#CA8A04] text-black py-3 rounded-full font-bold transition-all shadow-lg shadow-[#EAB308]/20"
            >
              Add Image to Gallery
            </button>
          </form>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((image, idx) => (
          <div
            key={image._id || image.id || `img-${idx}`}
            className="bg-[#1a4855] border border-[#EAB308]/20 rounded-2xl overflow-hidden group hover:border-primary/50 transition-all duration-300 flex flex-col"
          >
            <div className="relative h-64 bg-[#0d1c22]">
              <img
                src={getAdminImageUrl(image)}
                alt={image.caption}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/signature_persian_kabab_platter_1776082069527.png";
                }}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />

              {/* Category Badge */}
              <div className="absolute top-3 left-3">
                <span className="bg-black/70 backdrop-blur-sm border border-[#EAB308]/40 text-[#EAB308] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                  {image.category || "Ambience"}
                </span>
              </div>

              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                <button
                  onClick={() => handleDelete(image._id || image.id)}
                  title="Delete image"
                  className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full transition-all duration-300 shadow-lg scale-90 hover:scale-100"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 flex flex-col justify-between">
              <div>
                <p className="text-white font-bold text-base">{image.caption}</p>
                <p className="text-gray-400 text-xs mt-1">
                  Category: <span className="text-[#EAB308]">{image.category || "Ambience"}</span>
                </p>
              </div>
              <p className="text-gray-500 text-[11px] mt-2 border-t border-white/5 pt-2">
                Added: {image.created_at
                  ? new Date(image.created_at).toLocaleDateString()
                  : "Recently"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GalleryManagement;


